import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface MarketplaceFlags {
  marketplace_enabled: boolean;
  marketplace_public_enabled: boolean;
  marketplace_admin_preview_enabled: boolean;
  marketplace_ads_enabled: boolean;
  marketplace_top_sellers_enabled: boolean;
  marketplace_top_sellers_public_enabled: boolean;
  marketplace_existing_board_promotion_enabled: boolean;
  marketplace_allow_admin_transactions: boolean;
  marketplace_allow_public_indexing: boolean;
}

export const MARKETPLACE_FLAG_DEFAULTS: MarketplaceFlags = {
  marketplace_enabled: true,
  marketplace_public_enabled: false,
  marketplace_admin_preview_enabled: true,
  marketplace_ads_enabled: false,
  marketplace_top_sellers_enabled: true,
  marketplace_top_sellers_public_enabled: false,
  marketplace_existing_board_promotion_enabled: true,
  marketplace_allow_admin_transactions: true,
  marketplace_allow_public_indexing: false,
};

export function useMarketplaceFlags() {
  return useQuery({
    queryKey: ["marketplace-flags"],
    staleTime: 30_000,
    queryFn: async (): Promise<MarketplaceFlags> => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "marketplace_flags")
        .maybeSingle();
      return { ...MARKETPLACE_FLAG_DEFAULTS, ...((data?.value ?? {}) as Partial<MarketplaceFlags>) };
    },
  });
}

export type MarketplacePermission =
  | "marketplace.preview"
  | "marketplace.manage"
  | "marketplace.services.moderate"
  | "marketplace.orders.view"
  | "marketplace.orders.override"
  | "marketplace.disputes.manage"
  | "marketplace.payments.view"
  | "marketplace.wallet_refunds.manage"
  | "marketplace.payouts.freeze"
  | "marketplace.reviews.moderate"
  | "marketplace.comments.moderate"
  | "marketplace.ranks.manage"
  | "marketplace.heats.adjust"
  | "marketplace.ads.manage"
  | "marketplace.top_sellers.manage"
  | "marketplace.existing_boards.map"
  | "marketplace.categories.manage"
  | "marketplace.ambassadors.manage"
  | "marketplace.policies.manage"
  | "marketplace.audit.view"
  | "marketplace.feature_flags.manage";

export function useMarketplaceCan(permission: MarketplacePermission) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["mp-can", permission, user?.id ?? null],
    enabled: Boolean(user?.id),
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("mp_can", { _perm: permission });
      if (error) return false;
      return Boolean(data);
    },
  });
  // A disabled query stays "pending" forever; signed-out visitors simply have no permission.
  return { ...query, isLoading: user?.id ? query.isLoading : false, data: user?.id ? query.data : false };
}

/** Combined access resolver: public flag OR admin preview permission. */
export function useMarketplaceAccess() {
  const flags = useMarketplaceFlags();
  const canPreview = useMarketplaceCan("marketplace.preview");
  const isLoading = flags.isLoading || canPreview.isLoading;
  const isPublic = Boolean(flags.data?.marketplace_public_enabled);
  const preview = Boolean(flags.data?.marketplace_admin_preview_enabled && canPreview.data);
  return {
    flags: flags.data ?? MARKETPLACE_FLAG_DEFAULTS,
    isLoading,
    isPublic,
    canPreview: preview,
    allowed: isPublic || preview,
  };
}
